import { UserDeviceEntity } from '@finance-lab/utils/types'

export type ParsedUserDeviceDetails = {
    deviceKind: string | null
    os: string | null
    browser: string | null
    userAgent: string | null
    ipAddress: string | null
}

type JsonObject = Record<string, unknown>

function asObject(value: unknown): JsonObject | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as JsonObject) : null
}

export function getDeviceDetailsJson(details: UserDeviceEntity['details']): string {
    if (details === null || details === undefined) {
        return 'Ingen metadata hittades'
    }
    return JSON.stringify(details, null, 2)
}

function getSystemComponent(metadata: JsonObject | null): JsonObject | null {
    const components = asObject(metadata?.components)
    return asObject(components?.system)
}

function readUserAgent(system: JsonObject | null): string | null {
    const ua = system?.useragent ?? system?.userAgent
    if (typeof ua === 'string' && ua.length > 0) {
        return ua
    }
    return null
}

function inferDeviceKind(userAgent: string): string {
    const ua = userAgent.toLowerCase()
    if (/ipad|tablet|playbook|kindle|silk|(android(?!.*mobile))/i.test(userAgent)) {
        return 'Surfplatta'
    }
    if (/mobile|iphone|ipod|android.*mobile|windows phone|blackberry/i.test(ua)) {
        return 'Mobil'
    }
    return 'Dator'
}

function inferOs(platform: unknown, userAgent: string): string | null {
    const ua = userAgent.toLowerCase()
    if (/iphone|ipod|ipad/.test(ua)) {
        return 'iOS'
    }
    if (/android/.test(ua)) {
        return 'Android'
    }
    if (/windows nt|win32|win64/i.test(userAgent) || platform === 'Win32') {
        return 'Windows'
    }
    if (/macintosh|mac os x/i.test(userAgent) || platform === 'MacIntel') {
        return 'macOS'
    }
    if (/cros/.test(ua)) {
        return 'ChromeOS'
    }
    if (/linux/i.test(userAgent)) {
        return 'Linux'
    }
    return null
}

function readString(value: unknown): string | null {
    return typeof value === 'string' && value.length > 0 ? value : null
}

/** Thumbmark API: info.ip_address.ip_address (snake_case) or camelCase variants. */
function readThumbmarkIpAddress(metadata: JsonObject | null): string | null {
    const info = asObject(metadata?.info)
    if (!info) {
        return null
    }

    const direct = readString(info.ipAddress ?? info.ip_address)
    if (direct) {
        return direct
    }

    const nested = asObject(info.ipAddress ?? info.ip_address)
    return readString(nested?.ipAddress ?? nested?.ip_address)
}

function formatBrowser(system: JsonObject | null): string | null {
    const browser = asObject(system?.browser)
    const name = browser?.name
    const version = browser?.version
    if (typeof name === 'string' && name.length > 0) {
        return typeof version === 'string' && version.length > 0 ? `${name} ${version}` : name
    }
    return null
}

export function parseUserDeviceDetails(details: UserDeviceEntity['details']): ParsedUserDeviceDetails {
    const metadata = asObject(details)
    const system = getSystemComponent(metadata)
    const userAgent = readUserAgent(system)
    const ipAddress = readThumbmarkIpAddress(metadata)

    const ua = userAgent ?? ''
    const deviceKind = ua ? inferDeviceKind(ua) : null
    const os = ua ? inferOs(system?.platform, ua) : null
    const browser = formatBrowser(system)

    return {
        deviceKind,
        os,
        browser,
        userAgent,
        ipAddress,
    }
}
